import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Globe, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page" data-testid="privacy-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="legal-header">
            <Lock size={48} />
            <h1>Privacy Policy</h1>
            <p>How we collect, use, and protect your personal data</p>
            <p className="last-updated">Last updated: February 2026</p>
          </div>

          {/* GDPR Notice */}
          <div className="gdpr-notice">
            <Shield size={24} />
            <div>
              <h3>UK GDPR Compliant</h3>
              <p>
                This Privacy Policy complies with the UK General Data Protection Regulation (UK GDPR) 
                and the Data Protection Act 2018. We are committed to protecting your personal data 
                and your privacy rights.
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="legal-nav">
            <span>Quick links:</span>
            <a href="#data-controller">Data Controller</a>
            <a href="#what-we-collect">What We Collect</a>
            <a href="#how-we-use">How We Use Data</a>
            <a href="#your-rights">Your Rights</a>
            <a href="#cookies">Cookies</a>
            <a href="#contact">Contact</a>
          </div>

          {/* Section 1 */}
          <section id="data-controller" className="legal-section">
            <h2>1. Data Controller</h2>
            <p>
              BlackGirlTrading-Academy is the data controller responsible for your personal data. This means we 
              determine how and why your personal data is processed.
            </p>
            <div className="contact-box">
              <p><strong>BlackGirlTrading-Academy</strong></p>
              <p>Email: BlackGirlTradingAcademy@hotmail.com</p>
            </div>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact 
              us using the details above.
            </p>
          </section>

          {/* Section 2 */}
          <section id="what-we-collect" className="legal-section">
            <h2>2. What Personal Data We Collect</h2>
            
            <h3>2.1 Data You Provide Directly</h3>
            <div className="data-table">
              <div className="data-row">
                <div className="data-type">
                  <Database size={18} />
                  <span>Account Information</span>
                </div>
                <div className="data-details">
                  <p>Name, email address, password (encrypted)</p>
                </div>
              </div>
              <div className="data-row">
                <div className="data-type">
                  <Database size={18} />
                  <span>Payment Information</span>
                </div>
                <div className="data-details">
                  <p>Processed by Stripe. We do not store full card details. We receive: last 4 digits, card type, billing address</p>
                </div>
              </div>
              <div className="data-row">
                <div className="data-type">
                  <Database size={18} />
                  <span>Usage Data</span>
                </div>
                <div className="data-details">
                  <p>Paper trading activity, tutorials viewed, calculator usage (all within our platform)</p>
                </div>
              </div>
            </div>
            
            <h3>2.2 Data Collected Automatically</h3>
            <div className="data-table">
              <div className="data-row">
                <div className="data-type">
                  <Globe size={18} />
                  <span>Technical Data</span>
                </div>
                <div className="data-details">
                  <p>IP address, browser type, device information, operating system</p>
                </div>
              </div>
              <div className="data-row">
                <div className="data-type">
                  <Eye size={18} />
                  <span>Analytics Data</span>
                </div>
                <div className="data-details">
                  <p>Pages visited, time spent, click patterns, referral source</p>
                </div>
              </div>
            </div>
            
            <h3>2.3 Data We Do NOT Collect</h3>
            <ul>
              <li>Your real trading account information</li>
              <li>Your actual investment portfolio</li>
              <li>Your bank account details (only payment card via Stripe)</li>
              <li>Sensitive personal data (racial origin, political opinions, health data, etc.)</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section id="legal-basis" className="legal-section">
            <h2>3. Legal Basis for Processing</h2>
            <p>Under UK GDPR, we must have a valid legal basis to process your personal data:</p>
            
            <div className="basis-grid">
              <div className="basis-item">
                <h4>Contract</h4>
                <p>Processing necessary to provide our services to you (account management, subscription services)</p>
              </div>
              <div className="basis-item">
                <h4>Legitimate Interests</h4>
                <p>Processing necessary for our legitimate business interests (analytics, improving services, fraud prevention)</p>
              </div>
              <div className="basis-item">
                <h4>Consent</h4>
                <p>Where you have given specific consent (marketing emails, optional cookies)</p>
              </div>
              <div className="basis-item">
                <h4>Legal Obligation</h4>
                <p>Processing required to comply with the law (tax records, legal requests)</p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="how-we-use" className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use your personal data to:</p>
            <ul>
              <li>Create and manage your account</li>
              <li>Process subscription payments</li>
              <li>Provide access to educational content based on your subscription tier</li>
              <li>Save your paper trading portfolio and history</li>
              <li>Send service-related communications (password resets, subscription confirmations)</li>
              <li>Improve our platform and user experience</li>
              <li>Prevent fraud and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
            
            <h3>Marketing Communications</h3>
            <p>
              We will only send marketing emails if you have opted in. You can unsubscribe at any time 
              by clicking the link in our emails or contacting us.
            </p>
          </section>

          {/* Section 5 */}
          <section id="data-sharing" className="legal-section">
            <h2>5. Who We Share Your Data With</h2>
            <p>We may share your personal data with:</p>
            
            <div className="sharing-list">
              <div className="sharing-item">
                <h4>Stripe (Payment Processor)</h4>
                <p>To process subscription payments. Stripe has its own privacy policy and is PCI-DSS compliant.</p>
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>
              </div>
              <div className="sharing-item">
                <h4>Hosting Providers</h4>
                <p>Our platform is hosted on secure cloud infrastructure to deliver our services.</p>
              </div>
              <div className="sharing-item">
                <h4>Legal Authorities</h4>
                <p>When required by law, court order, or to protect our legal rights.</p>
              </div>
            </div>
            
            <p><strong>We do NOT sell your personal data to third parties.</strong></p>
          </section>

          {/* Section 6 */}
          <section id="data-retention" className="legal-section">
            <h2>6. Data Retention</h2>
            <p>We retain your personal data for as long as necessary to:</p>
            <ul>
              <li>Provide our services to you</li>
              <li>Comply with legal obligations (e.g., tax records for 7 years)</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>
            
            <h3>Retention Periods</h3>
            <div className="retention-table">
              <div className="retention-row">
                <span>Account data</span>
                <span>Until account deletion + 30 days</span>
              </div>
              <div className="retention-row">
                <span>Payment records</span>
                <span>7 years (legal requirement)</span>
              </div>
              <div className="retention-row">
                <span>Analytics data</span>
                <span>26 months</span>
              </div>
              <div className="retention-row">
                <span>Marketing preferences</span>
                <span>Until you unsubscribe</span>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section id="your-rights" className="legal-section">
            <h2>7. Your Rights Under UK GDPR</h2>
            <p>You have the following rights regarding your personal data:</p>
            
            <div className="rights-grid">
              <div className="right-item">
                <h4>Right of Access</h4>
                <p>Request a copy of the personal data we hold about you</p>
              </div>
              <div className="right-item">
                <h4>Right to Rectification</h4>
                <p>Request correction of inaccurate or incomplete data</p>
              </div>
              <div className="right-item">
                <h4>Right to Erasure</h4>
                <p>Request deletion of your personal data ("right to be forgotten")</p>
              </div>
              <div className="right-item">
                <h4>Right to Restrict Processing</h4>
                <p>Request that we limit how we use your data</p>
              </div>
              <div className="right-item">
                <h4>Right to Data Portability</h4>
                <p>Receive your data in a structured, machine-readable format</p>
              </div>
              <div className="right-item">
                <h4>Right to Object</h4>
                <p>Object to processing based on legitimate interests or for marketing</p>
              </div>
              <div className="right-item">
                <h4>Right to Withdraw Consent</h4>
                <p>Withdraw consent at any time where processing is based on consent</p>
              </div>
              <div className="right-item">
                <h4>Right to Complain</h4>
                <p>Lodge a complaint with the Information Commissioner's Office (ICO)</p>
              </div>
            </div>
            
            <p>
              To exercise any of these rights, please contact us at BlackGirlTradingAcademy@hotmail.com. 
              We will respond within one month as required by UK GDPR.
            </p>
          </section>

          {/* Section 8 */}
          <section id="cookies" className="legal-section">
            <h2>8. Cookies</h2>
            <p>
              We use cookies and similar technologies to provide and improve our services.
            </p>
            
            <h3>Essential Cookies</h3>
            <p>
              Required for the platform to function (authentication, security). 
              These cannot be disabled.
            </p>
            
            <h3>Analytics Cookies</h3>
            <p>
              Help us understand how visitors use our platform. 
              You can opt out of these.
            </p>
            
            <h3>Managing Cookies</h3>
            <p>
              You can control cookies through your browser settings. Note that disabling 
              certain cookies may affect the functionality of our platform.
            </p>
          </section>

          {/* Section 9 */}
          <section id="international-transfers" className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside the UK. 
              When this happens, we ensure appropriate safeguards are in place, such as:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by the ICO</li>
              <li>Adequacy decisions (where the destination country provides adequate protection)</li>
              <li>Binding Corporate Rules (for transfers within corporate groups)</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section id="security" className="legal-section">
            <h2>10. Data Security</h2>
            <p>We implement appropriate security measures to protect your personal data:</p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Encrypted password storage (bcrypt hashing)</li>
              <li>Secure payment processing through Stripe</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
            </ul>
            <p>
              While we take reasonable precautions, no method of transmission over the Internet 
              is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 11 */}
          <section id="children" className="legal-section">
            <h2>11. Children's Privacy</h2>
            <p>
              Our platform is not intended for children under 18 years of age. We do not 
              knowingly collect personal data from children. If you believe we have collected 
              data from a child, please contact us immediately.
            </p>
          </section>

          {/* Section 12 */}
          <section id="changes" className="legal-section">
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of 
              significant changes by email or through our platform. The "Last updated" date 
              at the top indicates when the policy was last revised.
            </p>
          </section>

          {/* Section 13 */}
          <section id="contact" className="legal-section">
            <h2>13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or wish to exercise your rights:</p>
            <div className="contact-box">
              <p><strong>BlackGirlTrading-Academy - Data Protection</strong></p>
              <p>Email: BlackGirlTradingAcademy@hotmail.com</p>
            </div>
            
            <h3>Information Commissioner's Office (ICO)</h3>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint 
              with the UK supervisory authority:
            </p>
            <div className="contact-box">
              <p><strong>Information Commissioner's Office</strong></p>
              <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a></p>
              <p>Phone: 0303 123 1113</p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="legal-footer-links">
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/pricing">Pricing</Link>
          </div>
        </motion.div>
      </div>

      <style>{`
        .legal-page {
          padding: 40px 0 80px;
        }

        .legal-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .legal-header svg {
          color: var(--primary);
          margin-bottom: 16px;
        }

        .legal-header h1 {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }

        .legal-header p {
          color: var(--text-secondary);
        }

        .last-updated {
          font-size: 14px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .gdpr-notice {
          display: flex;
          gap: 16px;
          background: rgba(0, 255, 148, 0.05);
          border: 1px solid rgba(0, 255, 148, 0.3);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .gdpr-notice svg {
          color: var(--success);
          flex-shrink: 0;
          margin-top: 4px;
        }

        .gdpr-notice h3 {
          margin-bottom: 8px;
          color: var(--success);
        }

        .gdpr-notice p {
          color: var(--text-secondary);
          margin: 0;
          line-height: 1.6;
        }

        .legal-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding: 16px 24px;
          background: var(--bg-paper);
          border-radius: 12px;
          margin-bottom: 32px;
          align-items: center;
        }

        .legal-nav span {
          color: var(--text-muted);
          font-size: 14px;
        }

        .legal-nav a {
          color: var(--primary);
          text-decoration: none;
          font-size: 14px;
        }

        .legal-nav a:hover {
          text-decoration: underline;
        }

        .legal-section {
          margin-bottom: 40px;
          padding-bottom: 40px;
          border-bottom: 1px solid var(--border);
        }

        .legal-section:last-of-type {
          border-bottom: none;
        }

        .legal-section h2 {
          font-size: 1.5rem;
          margin-bottom: 20px;
          color: var(--primary-light);
        }

        .legal-section h3 {
          font-size: 1.1rem;
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .legal-section h4 {
          font-size: 1rem;
          margin-bottom: 8px;
        }

        .legal-section p {
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 16px;
        }

        .legal-section ul {
          margin: 16px 0;
          padding-left: 24px;
        }

        .legal-section li {
          color: var(--text-secondary);
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .legal-section a {
          color: var(--primary);
        }

        .contact-box {
          background: var(--bg-subtle);
          padding: 20px;
          border-radius: 8px;
          margin: 16px 0;
        }

        .contact-box p {
          margin: 4px 0;
        }

        /* Data Table */
        .data-table {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin: 16px 0;
        }

        .data-row {
          display: flex;
          gap: 24px;
          padding: 16px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
        }

        .data-type {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 200px;
          font-weight: 500;
        }

        .data-type svg {
          color: var(--primary);
        }

        .data-details p {
          margin: 0;
          font-size: 14px;
        }

        /* Basis Grid */
        .basis-grid, .rights-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin: 24px 0;
        }

        .basis-item, .right-item {
          padding: 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
        }

        .basis-item h4, .right-item h4 {
          color: var(--primary-light);
          margin-bottom: 8px;
        }

        .basis-item p, .right-item p {
          font-size: 14px;
          margin: 0;
        }

        /* Sharing List */
        .sharing-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin: 16px 0;
        }

        .sharing-item {
          padding: 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
        }

        .sharing-item h4 {
          margin-bottom: 8px;
        }

        .sharing-item p {
          font-size: 14px;
          margin-bottom: 8px;
        }

        .sharing-item a {
          font-size: 13px;
        }

        /* Retention Table */
        .retention-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 16px 0;
        }

        .retention-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 14px;
        }

        .retention-row span:first-child {
          font-weight: 500;
        }

        .retention-row span:last-child {
          color: var(--text-muted);
        }

        .legal-footer-links {
          display: flex;
          justify-content: center;
          gap: 32px;
          padding-top: 32px;
          border-top: 1px solid var(--border);
        }

        .legal-footer-links a {
          color: var(--text-muted);
          text-decoration: none;
        }

        .legal-footer-links a:hover {
          color: var(--primary);
        }

        @media (max-width: 768px) {
          .legal-nav {
            flex-direction: column;
            align-items: flex-start;
          }

          .data-row {
            flex-direction: column;
            gap: 8px;
          }

          .data-type {
            min-width: auto;
          }

          .basis-grid, .rights-grid {
            grid-template-columns: 1fr;
          }

          .gdpr-notice {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;
